import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { authHeaders } from "@/lib/api/_shared";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const DOCUMENT_TYPES = [
  { value: "purchase_order", label: "Purchase order" },
  { value: "invoice",        label: "Invoice" },
  { value: "manifest",       label: "Manifest" },
];

/**
 * DocAIPanel — file picker + "Parse" button that POSTs a JPG/PNG/PDF to
 * the FastAPI /api/parse-document endpoint (GPT-4o vision via Emergent
 * LLM key). On success, calls onParsed(extracted, filename) so the
 * parent form can autofill its fields.
 */
export default function DocAIPanel({ onParsed }) {
  const [docType, setDocType] = useState("purchase_order");
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState(null);
  const fileRef = useRef(null);

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    const okType = ["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(file.type);
    if (!okType) {
      toast.error(`Unsupported file type: ${file.type}`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10 MB");
      return;
    }
    setFileName(file.name);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", docType);
      const res = await fetch(`${API_BASE}/api/parse-document`, { method: "POST", body: fd, headers: await authHeaders() });
      // Content-Type guard — if the ingress ever serves the SPA index
      // instead of our JSON (as happened on prod pre-support-fix), surface
      // a clean error rather than a raw JSON parse crash.
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        toast.error(
          "Doc-AI is temporarily unreachable. Please try again in a minute or fill the form manually."
        );
        return;
      }
      const json = await res.json();
      if (!res.ok) {
        toast.error(json?.error || json?.detail || `Parse failed (${res.status})`);
        return;
      }
      onParsed?.(json.extracted || {}, file.name);
    } catch (e) {
      toast.error(`Parse failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="doc-ai-panel"
      className="rounded-lg border bg-gradient-to-br from-violet-50 to-blue-50 p-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-violet-600" />
        <span className="text-sm font-medium">Auto-fill from document photo</span>
      </div>
      <p className="text-xs text-slate-600 mb-3">
        Upload a clear photo or PDF of a PO, invoice, or manifest. GPT-4o reads the document and
        fills the matching fields below (you can edit anything before saving).
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[180px]">
          <Select value={docType} onValueChange={setDocType} disabled={busy}>
            <SelectTrigger data-testid="doc-ai-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={handlePick}
          disabled={busy}
          data-testid="doc-ai-pick-btn"
        >
          <Upload className="h-4 w-4 mr-1.5" />
          {busy ? "Parsing…" : "Choose file & parse"}
        </Button>
        {fileName && !busy && (
          <span className="text-xs text-slate-500 truncate max-w-xs" data-testid="doc-ai-file-name">
            {fileName}
          </span>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        data-testid="doc-ai-file-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}
